/**
 * PDP About slider
 *
 * Wires the dot navigation under the About-section lifestyle image. The Liquid
 * (`sections/pdp-about.liquid`) renders a single `<img class="about-media-img">`,
 * a row of `.about-media-dot[data-pdp-about-dot="<index>"]` buttons and a JSON
 * list of image URLs in `<script data-pdp-about-images>`. There is no autoplay —
 * this is a manual, click-to-swap slider (dots below the image, hero pattern).
 *
 * Clicking a dot swaps `img.src` to the matching URL and moves the active state
 * across the dots. Scoped per `[data-pdp-about-slider]` wrapper so multiple
 * instances on one page stay independent. NGE-8051.
 *
 * The last slide can be a video instead of an image. Liquid then puts its index in
 * `data-pdp-about-video-index` on the wrapper and renders a `[data-pdp-about-video]`
 * panel inside the media frame; activating that index reveals the panel and hides the
 * image. The URL list holds images only, so `images[videoIndex]` is undefined and no
 * src swap happens on the video slide. NGE-9614.
 */
(function initPdpAboutSliders() {
  const wire = (slider) => {
    const img = slider.querySelector('.about-media-img');
    const dots = Array.from(slider.querySelectorAll('[data-pdp-about-dot]'));
    if (!img || dots.length < 2) return;

    const frame = slider.querySelector('.about-media--slider');
    const rawVideoIndex = slider.dataset.pdpAboutVideoIndex;
    const videoIndex = rawVideoIndex === undefined ? -1 : parseInt(rawVideoIndex, 10);

    // Image URLs: prefer the JSON list Liquid emits; fall back to the dot count
    // (a dot with no matching URL just no-ops).
    let images = [];
    const json = slider.querySelector('[data-pdp-about-images]');
    if (json) {
      try {
        images = JSON.parse(json.textContent) || [];
      } catch (_) {
        images = [];
      }
    }

    let current = 0;

    // The URL list comes from DOM text (a JSON <script> block), so it is untrusted
    // input as far as the scanner — and a `javascript:`/`data:` value assigned to
    // src is a real sink. Allow only same-origin-ish image paths and absolute
    // http(s). (CodeQL: "DOM text reinterpreted as HTML", alert 21.)
    const isSafeImageUrl = (value) => {
      if (typeof value !== 'string' || value.trim() === '') return false;
      const url = value.trim();
      if (url.startsWith('/') && !url.startsWith('//')) return true;
      try {
        // A protocol-relative `//host/x.png` inherits the BASE scheme — whatever
        // the page is served over. That is https on a Shopify storefront, but on
        // an http page it resolves to http. Do not read this as an implicit https
        // upgrade; it is not one, and carries no mixed-content guarantee itself.
        //
        // Allowing it is still deliberate, not an oversight: these images live on
        // the Shopify CDN, so cross-origin absolute http(s) has to pass anyway,
        // which makes `//host/x` equivalent to an input we already accept. The
        // sink being guarded is `javascript:` / `data:` on `img.src`, and both
        // still fail the protocol check below whatever the page scheme is.
        const parsed = new URL(url, window.location.origin);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
      } catch (_) {
        return false;
      }
    };

    /**
     * Is slide `i` something we can actually show?
     *
     * Two ways to qualify, and the video slide is why this is not just a URL check: it
     * deliberately has NO entry in `images` (see the header — the list holds images
     * only) because the panel covers the frame instead of swapping `img.src`. Testing
     * the URL alone would classify the video slide as unusable and make it unreachable.
     */
    const isVideoSlide = (i) => frame !== null && videoIndex >= 0 && i === videoIndex;
    const isUsableSlide = (i) => isVideoSlide(i) || isSafeImageUrl(images[i]);

    /**
     * Move to slide `idx`.
     *
     * `step` is the direction of travel, and only prev/next pass it. It exists because
     * bailing on an unusable slide — correct in itself, since moving the dot while
     * leaving `img.src` behind makes the UI lie about what is on screen (PR #121
     * review) — turns into a dead end for the arrows: `next` onto an unusable slide
     * would do nothing at all, and the user could never reach the slide after it.
     * `dots` come from markup while `images` come from a JSON block, so a gap is
     * possible by construction; the file header already allows for it ("a dot with no
     * matching URL just no-ops").
     *
     * So the arrows skip PAST an unusable slide in the direction they were going, while
     * a dot click still refuses rather than quietly showing a different slide than the
     * dot the user pressed.
     */
    const activate = (idx, step = 0) => {
      const total = dots.length;
      let target = ((idx % total) + total) % total;

      if (!isUsableSlide(target)) {
        if (step === 0) return; // dot click: honour it or do nothing
        // Walk at most one full lap, so an all-unusable list terminates.
        let found = false;
        for (let hop = 1; hop < total; hop++) {
          const candidate = (((target + step * hop) % total) + total) % total;
          if (isUsableSlide(candidate)) {
            target = candidate;
            found = true;
            break;
          }
        }
        if (!found) return;
      }

      current = target;
      // Only swap the src for an image slide. The video slide has no URL, and
      // assigning `undefined` would blank the image behind the panel.
      if (isSafeImageUrl(images[target])) img.src = images[target];
      // Video slide: reveal the panel over the image frame. CSS owns the actual
      // show/hide so the iframe stays display:none — and therefore unfetched — until
      // the visitor asks for it.
      if (frame && videoIndex >= 0) {
        frame.classList.toggle('is-video-active', current === videoIndex);
      }
      for (let i = 0; i < dots.length; i++) {
        const isActive = i === current;
        dots[i].classList.toggle('is-active', isActive);
        if (isActive) dots[i].setAttribute('aria-current', 'true');
        else dots[i].removeAttribute('aria-current');
      }
    };

    for (const dot of dots) {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.pdpAboutDot ?? '0', 10);
        if (Number.isNaN(idx)) return;
        activate(idx);
      });
    }

    // Prev/next arrows (optional — wrap around). Dots stay the source of truth
    // for the active index via `current`.
    const prev = slider.querySelector('.about-media-arrow--prev');
    const next = slider.querySelector('.about-media-arrow--next');
    if (prev) prev.addEventListener('click', () => activate(current - 1, -1));
    if (next) next.addEventListener('click', () => activate(current + 1, 1));
  };

  const init = () => {
    for (const slider of document.querySelectorAll('[data-pdp-about-slider]')) {
      wire(slider);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
